import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Heading, Tab, TabList, TabPanel, Tabs } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  INITIATIVE_STATUSES,
  type ChangeEvent,
  type InitiativeAction,
  type InitiativeDetailResponse,
  type InputRequestDto,
  type ReadinessStateValue,
} from '@complan/contracts';
import { attemptTransition, type InitiativeStatus } from '@complan/domain';
import { useSession } from '@/app/session/session';
import { READINESS_STATE_LABEL_KEYS, STATUS_LABEL_KEYS } from '@/shared/i18n/enum-labels';
import { AriaModalDialog } from '@/shared/a11y/AriaDialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/ui/status-badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCalendarEntryFreshness } from '@/features/calendar/use-calendar-freshness';
import { formatDate, formatDateRange } from '@/shared/format/date-format';
import {
  flattenPages,
  useChangeHistory,
  useInitiativeDetail,
  useInputRequests,
} from './detail-data';
import {
  useCancelInitiative,
  useDeleteInitiative,
  useSubmitInputRequest,
  useTransitionInitiative,
} from './detail-actions';
import { InputRequestDialog } from './InputRequestDialog';
import { CancelInitiativeDialog } from './CancelInitiativeDialog';
import { DeleteInitiativeDialog } from './DeleteInitiativeDialog';
import { TransitionDialog } from './TransitionDialog';
import { useDetailReferenceData, type DetailReferenceData } from './detail-reference-data';
import { WorkloadTab } from './WorkloadTab';
import { WatchoutsTab } from './watchouts/WatchoutsTab';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/**
 * The Detail_Panel, mounted as a nested route under whichever screen lists
 * Initiatives: the calendar (`/calendar/:year/initiative/:id`) and the Initiative
 * list (`/initiatives/:id`). It is a true dialog via React Aria (focus trap on
 * open, focus restore on close, `Esc` to dismiss) per design.md's accessibility
 * notes and R18.6.
 *
 * Closing returns to the parent route while preserving the search string, so the
 * filters, orientation and sort the user had are still applied — the parent never
 * unmounted, so this is a URL change, not a rebuild (R11.13). React Aria's `Modal`
 * restores focus to the element that opened it — the triggering Initiative bar or
 * table row link — on close, the focus-restore the UI spec §7/§9 flags as a
 * mandatory addition over the click dummy.
 *
 * ## Why the parent is resolved, not named
 *
 * This component knows it is *a* nested route, not *which* one. Both hosts keep
 * their view state in the search params, so both need the same "up one route,
 * same query" close, and hardcoding `/calendar/:year` would send a panel opened
 * from the Initiative list to the calendar instead of back to the list.
 *
 * The drawer anatomy follows UI spec §7: a right-side 620px surface that slides
 * in over 0.22s with the `-2px 0 14px rgba(0,0,0,.15)` shadow behind a
 * `rgba(21,42,71,.35)` scrim, and three regions — a fixed header (ID, title,
 * badge row, tab bar), a scrolling body, and a fixed `--surface-alt` footer.
 *
 * The five tabs are Overview, Workload, Milestones, Dependencies and History, per
 * UI spec §7. The Workload tab was out of scope while the manpower engine was
 * deferred; it now reads that engine (`POST /api/manpower/demand`) and is
 * capability-gated on `manpower:read` — see {@link WorkloadTab}.
 *
 * ## Names, not identifiers
 *
 * `GET /api/initiatives/:id` returns association ids. Every tab resolves them
 * through one {@link useDetailReferenceData} call threaded down as `reference`,
 * so the Overview's classification, the Workload chain's activity/segment labels
 * and the Sales_Cycle sets all read from one cache and cannot disagree.
 *
 * ## Outdated-entry marking (task 22.7, R13.5) and the version seam
 *
 * The detail read returns the current record `version`; that value is passed into
 * {@link useCalendarEntryFreshness}, which compares it to the version the calendar
 * bar carried, invalidates the calendar so the bar re-renders from current values
 * within 3s, and returns `isOutdated` for the indication below. Until the detail
 * resolves the version is `null` and the hook is inert.
 */
export function DetailPanelRoute(): React.JSX.Element {
  const params = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const initiativeId = params['id'] ?? '';

  const detailQuery = useInitiativeDetail(initiativeId);
  const detail = detailQuery.data;

  // SEAM (task 23.4): the version the Detail_Panel content read returns feeds the
  // freshness hook. The comparison against the calendar bar's version and the
  // re-render on staleness are wired in the hook (R13.5).
  const detailVersion: number | null = detail?.version ?? null;
  const { isOutdated } = useCalendarEntryFreshness(initiativeId, detailVersion);

  /**
   * Returns to the route that hosts this panel, keeping the query string.
   *
   * `'..'` is resolved against the *route* hierarchy (React Router's default
   * `relative: 'route'`), not against URL segments, so it lands on the parent
   * route whatever its path length: `/calendar/:year` for the two-segment
   * `initiative/:id` child, `/initiatives` for the one-segment `:id` child.
   */
  const close = (): void => {
    const query = search.toString();
    navigate({ pathname: '..', search: query ? `?${query}` : '' });
  };

  /**
   * Navigates to this Initiative's edit route. Not nested under the calendar
   * (unlike `close`): the edit form is a full-page wizard like
   * `/intake`, not a drawer, so there is no filter/orientation/sort state of the
   * calendar to preserve here.
   */
  const editInitiative = (): void => {
    navigate(`/initiative/${initiativeId}/edit`);
  };

  return (
    <AriaModalDialog
      variant="drawer"
      isOpen
      unpadded
      // UI spec §7: 620px surface, 0.22s slide, exact drawer shadow and scrim.
      modalClassName="w-[620px] shadow-[-2px_0_14px_rgba(0,0,0,0.15)] duration-[220ms]"
      overlayClassName="bg-[rgba(21,42,71,0.35)] backdrop-blur-0"
      onOpenChange={(open) => {
        if (!open) close();
      }}
      aria-label={t('detail.dialogLabel')}
    >
      {detailQuery.isLoading ? (
        <PanelStatus>{t('detail.loading')}</PanelStatus>
      ) : detail === undefined ? (
        <PanelStatus>{t('detail.notFound')}</PanelStatus>
      ) : (
        <PanelContent
          detail={detail}
          initiativeId={initiativeId}
          isOutdated={isOutdated}
          onClose={close}
          onEdit={editInitiative}
        />
      )}
    </AriaModalDialog>
  );
}

/** A centred status line for the loading and not-found states. */
function PanelStatus({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="text-muted-foreground flex h-full items-center justify-center p-6 text-[12.3px]">
      {children}
    </div>
  );
}

/**
 * The mutation actions that must never render for a Read_Only_User (R12.8).
 *
 * `transition` is present but is not rendered as a single button: the footer
 * expands it into one button per *permitted target status* (Submit, Plan,
 * Approve, …), because R9's lifecycle is a set of named moves, not one generic
 * "transition" verb. See {@link permittedTransitionTargets} and the footer.
 */
const RENDERABLE_ACTIONS: readonly InitiativeAction[] = [
  'edit',
  'transition',
  'cancel',
  'delete',
  'input_request',
];

/**
 * The status transitions the footer offers as buttons, given the record's
 * current status and whether the acting principal holds approver rights (R9.3,
 * R9.12).
 *
 * Each candidate target is put to the domain's own transition table
 * ({@link attemptTransition}) — the same table the server enforces — so the
 * client offers exactly the moves the server would permit for this principal, and
 * an approver-gated target (Approve, Final, and moves out of Approved/Final)
 * simply does not appear for a non-approver. `cancelled` is excluded here because
 * cancellation keeps its own dedicated destructive button and dialog; every other
 * permitted target becomes a button. Each carries `requiresReason`, so the dialog
 * knows whether to demand a reason (all but Draft → Submitted).
 */
function permittedTransitionTargets(
  currentStatus: InitiativeStatus,
  hasApproverRights: boolean,
): readonly { readonly target: InitiativeStatus; readonly requiresReason: boolean }[] {
  const targets: { target: InitiativeStatus; requiresReason: boolean }[] = [];
  for (const candidate of INITIATIVE_STATUSES) {
    if (candidate === currentStatus || candidate === 'cancelled') {
      continue;
    }
    const result = attemptTransition(currentStatus, candidate, hasApproverRights);
    if (result.permitted) {
      targets.push({ target: candidate, requiresReason: result.requiresReason });
    }
  }
  return targets;
}

/**
 * The three drawer regions: a fixed header, a scrolling tabbed body and a fixed
 * footer holding the capability-gated actions.
 */
function PanelContent({
  detail,
  initiativeId,
  isOutdated,
  onClose,
  onEdit,
}: {
  readonly detail: InitiativeDetailResponse;
  readonly initiativeId: string;
  readonly isOutdated: boolean;
  readonly onClose: () => void;
  readonly onEdit: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const reference = useDetailReferenceData(detail);

  return (
    <Tabs className="flex h-full flex-col">
      {/* Fixed header: ID, close, title, badge row, tab bar (UI spec §7). */}
      <header className="border-line shrink-0 border-b px-4 pt-3">
        <div className="flex items-start justify-between">
          <p className="text-muted-foreground text-[11.5px]">
            {t('detail.reference', { id: detail.initiativeRef })}
          </p>
          <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t('detail.close')}>
            <span aria-hidden>{'\u00d7'}</span>
          </Button>
        </div>
        <Heading slot="title" className="mt-0.5 text-[16px] font-semibold text-txt">
          {detail.title}
        </Heading>
        <BadgeRow detail={detail} />
        <TabList
          aria-label={t('detail.dialogLabel')}
          className="mt-3 flex gap-1 overflow-x-auto"
        >
          <DrawerTab id="overview" label={t('detail.tab.overview')} />
          <DrawerTab id="workload" label={t('detail.tab.workload')} />
          <DrawerTab id="milestones" label={t('detail.tab.milestones')} />
          <DrawerTab id="dependencies" label={t('detail.tab.dependencies')} />
          {/* `complan-governance` R7.9. Placed before History because a watchout is
              current state a reader acts on, where the history is the record's past. */}
          <DrawerTab id="watchouts" label={t('detail.tab.watchouts')} />
          <DrawerTab id="history" label={t('detail.tab.history')} />
        </TabList>
      </header>

      {/* Scrolling body. */}
      <div className="min-h-0 flex-1 overflow-auto px-4 py-4">
        {isOutdated ? <OutdatedIndication /> : null}
        <TabPanel id="overview" className="outline-none">
          <OverviewTab detail={detail} reference={reference} />
        </TabPanel>
        <TabPanel id="workload" className="outline-none">
          <WorkloadTab detail={detail} reference={reference} />
        </TabPanel>
        <TabPanel id="milestones" className="outline-none">
          <MilestonesTab detail={detail} />
        </TabPanel>
        <TabPanel id="dependencies" className="outline-none">
          <DependenciesTab detail={detail} />
        </TabPanel>
        <TabPanel id="watchouts" className="outline-none">
          <WatchoutsTab initiativeId={initiativeId} />
        </TabPanel>
        <TabPanel id="history" className="outline-none">
          <HistoryTab initiativeId={initiativeId} />
        </TabPanel>
      </div>

      {/* Fixed footer with the capability-gated actions. */}
      <PanelFooter
        detail={detail}
        initiativeId={initiativeId}
        onClosePanel={onClose}
        onEdit={onEdit}
      />
    </Tabs>
  );
}

/** One tab in the drawer tab bar, styled from the §7 active-tab treatment. */
function DrawerTab({ id, label }: { readonly id: string; readonly label: string }): React.JSX.Element {
  return (
    <Tab
      id={id}
      className={({ isSelected }) =>
        cn(
          'cursor-pointer rounded-t-[4px] px-3 py-1.5 text-[12.3px] font-medium outline-none',
          isSelected
            ? 'bg-accent-soft text-accent-hover border-accent-line border-b-2'
            : 'text-muted-foreground hover:text-txt',
        )
      }
    >
      {label}
    </Tab>
  );
}

/** The header badge row: status, priority, confidential and involvement badges. */
function BadgeRow({ detail }: { readonly detail: InitiativeDetailResponse }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="detail-badge-row">
      <StatusBadge status={detail.status} />
      {detail.isConfidential ? (
        <Badge variant="destructive">{t('detail.field.confidential')}</Badge>
      ) : null}
      {detail.fieldForceInvolvement ? (
        <Badge variant="outline">{t('detail.involvement.fieldForce')}</Badge>
      ) : null}
      {detail.dteInvolvement ? <Badge variant="outline">{t('detail.involvement.dte')}</Badge> : null}
      {detail.cscInvolvement ? <Badge variant="outline">{t('detail.involvement.csc')}</Badge> : null}
    </div>
  );
}

/**
 * The Overview tab: the `.kv` definition grid (`160px 1fr`) plus the
 * changed-after-approval flag, the manual Sales_Cycle override marker and the
 * reference links.
 */
function OverviewTab({
  detail,
  reference,
}: {
  readonly detail: InitiativeDetailResponse;
  readonly reference: DetailReferenceData;
}): React.JSX.Element {
  const { t } = useTranslation();
  const none = t('detail.value.none');

  const primaryPlatformId = detail.platforms.find((link) => link.isPrimary)?.platformId ?? null;
  const additionalPlatformIds = detail.platforms
    .filter((link) => !link.isPrimary)
    .map((link) => link.platformId);

  return (
    <div className="space-y-4">
      {detail.changedAfterApproval ? <ChangedAfterApproval detail={detail} /> : null}

      <dl className="grid grid-cols-[160px_1fr] gap-x-2.5 gap-y-1.5 text-[12.3px]">
        <KvRow label={t('detail.field.title')} value={detail.title} />
        <KvRow label={t('detail.field.details')} value={detail.details ?? none} />
        {/* Classification, matching the dummy's Overview order: Platform first,
            then category/type, then timing and Sales_Cycle. */}
        <KvRow
          label={t('detail.field.primaryPlatform')}
          value={primaryPlatformId === null ? none : reference.nameOf('platform', primaryPlatformId)}
        />
        {additionalPlatformIds.length > 0 ? (
          <KvRow
            label={t('detail.field.additionalPlatforms')}
            value={reference.namesOf('platform', additionalPlatformIds, none)}
          />
        ) : null}
        <KvRow
          label={t('detail.field.categories')}
          value={reference.namesOf('category', detail.categoryIds, none)}
        />
        <KvRow
          label={t('detail.field.initiativeTypes')}
          value={reference.namesOf('initiative-type', detail.initiativeTypeIds, none)}
        />
        {detail.cscInvolvement ? (
          <KvRow
            label={t('detail.field.cscAttributes')}
            value={reference.namesOf('csc-attribute', detail.cscAttributeIds, none)}
          />
        ) : null}
        <KvRow
          label={t('detail.field.timing')}
          value={formatDateRange(detail.startDate, detail.endDate)}
        />
        <KvRow label={t('detail.field.planningYear')} value={String(detail.planningYear)} />
        <KvRow
          label={t('detail.field.salesCycles')}
          value={reference.salesCycleNames(detail.salesCycleAssignment.assignedSalesCycleIds, none)}
        />
        <KvRow
          label={t('detail.field.salesCycleSource')}
          value={t(`detail.salesCycle.source.${detail.salesCycleAssignment.source}`)}
        />
        <KvRow
          label={t('detail.field.audiences')}
          value={reference.namesOf('audience', detail.audienceIds, none)}
        />
        <KvRow
          label={t('detail.field.channels')}
          value={reference.namesOf('channel', detail.channelIds, none)}
        />
        {detail.customerGroupIds.length > 0 ? (
          <KvRow
            label={t('detail.field.customerGroups')}
            value={reference.namesOf('customer-group', detail.customerGroupIds, none)}
          />
        ) : null}
        <KvRow
          label={t('detail.field.owner')}
          value={ownerLabel(t, detail.ownerDisplayName, detail.ownerAccountActive)}
        />
        <KvRow
          label={t('detail.field.backupOwner')}
          value={
            detail.backupOwnerDisplayName === null
              ? none
              : ownerLabel(t, detail.backupOwnerDisplayName, detail.backupOwnerAccountActive ?? true)
          }
        />
        <KvRow
          label={t('detail.field.priority')}
          value={reference.nameOf('priority', detail.priorityId)}
        />
        <KvRow label={t('detail.field.status')} value={t(STATUS_LABEL_KEYS[detail.status])} />
        <KvRow
          label={t('detail.field.confidential')}
          value={detail.isConfidential ? t('detail.value.yes') : t('detail.value.no')}
        />
        <KvRow label={t('detail.field.involvement')} value={involvementLabel(t, detail)} />
        <KvRow label={t('detail.field.version')} value={String(detail.version)} />
        <KvRow
          label={t('detail.field.created')}
          value={t('detail.value.byAt', {
            actor: detail.createdByDisplayName,
            at: formatDate(detail.createdAt.slice(0, 10)),
          })}
        />
        <KvRow
          label={t('detail.field.modified')}
          value={t('detail.value.byAt', {
            actor: detail.modifiedByDisplayName,
            at: formatDate(detail.modifiedAt.slice(0, 10)),
          })}
        />
        {detail.cancellationReason !== null ? (
          <KvRow
            label={t('detail.field.cancellationReason')}
            value={detail.cancellationReason}
          />
        ) : null}
      </dl>

      {detail.salesCycleAssignment.source === 'manual' ? (
        <ManualOverrideMarker assignment={detail.salesCycleAssignment} />
      ) : null}

      <ReferenceLinks links={detail.referenceLinks} />
    </div>
  );
}

/** One key/value row of the `.kv` grid (muted key, value). */
function KvRow({ label, value }: { readonly label: string; readonly value: string }): React.JSX.Element {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-txt">{value}</dd>
    </>
  );
}

/** The changed-after-approval flag with the actor and timestamp of the change (R12.10). */
function ChangedAfterApproval({
  detail,
}: {
  readonly detail: InitiativeDetailResponse;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Alert data-testid="detail-changed-after-approval" className="border-warn">
      <AlertTitle>{t('detail.changedAfterApproval.title')}</AlertTitle>
      <AlertDescription>
        <p>{t('detail.changedAfterApproval.body')}</p>
        {detail.changedAfterApprovalByDisplayName !== null ? (
          <p className="text-[11.5px]">
            {t('detail.changedAfterApproval.actor')}: {detail.changedAfterApprovalByDisplayName}
          </p>
        ) : null}
        {detail.changedAfterApprovalAt !== null ? (
          <p className="text-[11.5px]">
            {t('detail.changedAfterApproval.at')}:{' '}
            {formatDate(detail.changedAfterApprovalAt.slice(0, 10))}
          </p>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/**
 * The manual Sales_Cycle override marker (R7.6): the reason, actor and timestamp
 * of the override, plus the manually assigned set and the currently derived set.
 */
function ManualOverrideMarker({
  assignment,
}: {
  readonly assignment: InitiativeDetailResponse['salesCycleAssignment'];
}): React.JSX.Element {
  const { t } = useTranslation();
  const none = t('detail.value.none');
  return (
    <section
      data-testid="detail-manual-override"
      className="border-warn bg-warn/5 rounded-[4px] border p-3"
    >
      <h3 className="text-[12.3px] font-semibold text-txt">{t('detail.override.title')}</h3>
      <dl className="mt-2 grid grid-cols-[140px_1fr] gap-x-2.5 gap-y-1.5 text-[12.3px]">
        <KvRow label={t('detail.override.reason')} value={assignment.overrideReason ?? none} />
        <KvRow label={t('detail.override.actor')} value={assignment.overrideByDisplayName ?? none} />
        <KvRow
          label={t('detail.override.at')}
          value={assignment.overrideAt === null ? none : formatDate(assignment.overrideAt.slice(0, 10))}
        />
        <KvRow
          label={t('detail.override.manualSet')}
          value={setLabel(assignment.assignedSalesCycleIds, none)}
        />
        <KvRow
          label={t('detail.override.derivedSet')}
          value={setLabel(assignment.derivedSalesCycleIds, none)}
        />
      </dl>
    </section>
  );
}

/** The reference-link list (R12.4): a labelled anchor per stored link, up to 20. */
function ReferenceLinks({
  links,
}: {
  readonly links: InitiativeDetailResponse['referenceLinks'];
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section>
      <h3 className="text-[12.3px] font-semibold text-txt">{t('detail.referenceLinks.title')}</h3>
      {links.length === 0 ? (
        <p className="text-muted-foreground mt-1 text-[12.3px]">{t('detail.referenceLinks.empty')}</p>
      ) : (
        <ul className="mt-1 space-y-1 text-[12.3px]">
          {links.map((link) => (
            <li key={link.id}>
              <a
                href={link.url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-accent-hover underline"
              >
                {link.label ?? link.url}
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The Milestones tab: a table of type, planned date, completion and readiness state
 * (R12.2; `complan-governance` R8.3, R8.6, R8.10), ordered by ascending planned date as
 * the server returns them.
 *
 * ## The state is the server's, and that replaced a local derivation
 *
 * This tab used to compute the state itself: completed if a completion date existed,
 * otherwise overdue if the planned date was before the browser's idea of today. That was
 * a *third* reading of readiness beside the dashboards' and the filter's, and it could
 * disagree with both — it knew nothing of the Readiness_Horizon, so a milestone the
 * Governance_Dashboard counted as Due here read as Planned, and it compared against the
 * reader's clock rather than the server's date.
 *
 * `complan-governance` R8.6 requires one definition for the panel, both dashboards, the
 * R6.12 filter dimension and every export, "replacing the presentation-only derivation the
 * Detail_Panel applies today". So the classification arrives on each milestone and this
 * component renders it. The same reasoning the Dependencies tab already followed for
 * `isOpen`.
 *
 * ## Five states, and a sixth thing that is not a state
 *
 * R8.3's five are Completed, Planned, Due, Overdue and Missing. A milestone whose *type*
 * is not mandatory carries no state at all (`readinessState: null`, `isRequired: false`):
 * R8.10 requires it shown in the panel and excluded from every readiness figure, so it is
 * marked as not required rather than given a state it does not have.
 */
function MilestonesTab({ detail }: { readonly detail: InitiativeDetailResponse }): React.JSX.Element {
  const { t } = useTranslation();
  const none = t('detail.value.none');
  const milestones = [...detail.milestones].sort((a, b) => a.plannedDate.localeCompare(b.plannedDate));

  if (milestones.length === 0) {
    return <p className="text-muted-foreground text-[12.3px]">{t('detail.milestones.empty')}</p>;
  }

  return (
    <Table data-testid="detail-milestones">
      <TableHeader>
        <TableRow>
          <TableHead scope="col">{t('detail.milestones.colType')}</TableHead>
          <TableHead scope="col">{t('detail.milestones.colPlanned')}</TableHead>
          <TableHead scope="col">{t('detail.milestones.colCompleted')}</TableHead>
          <TableHead scope="col">{t('detail.milestones.colState')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {milestones.map((m) => (
          <TableRow key={m.id}>
            <TableCell className="text-txt">{m.milestoneTypeName ?? m.milestoneTypeId}</TableCell>
            <TableCell className="tabular-nums">{formatDate(m.plannedDate)}</TableCell>
            <TableCell className="tabular-nums">
              {m.completedDate === null ? none : formatDate(m.completedDate)}
            </TableCell>
            <TableCell data-testid="milestone-state">
              {m.readinessState === null ? (
                // R8.10: recorded, shown, and outside every readiness figure. Stated as
                // such rather than left blank, which would read as a missing value.
                <Badge variant="outline">{t('detail.milestones.notRequired')}</Badge>
              ) : (
                <Badge variant={readinessBadgeVariant(m.readinessState)}>
                  {t(READINESS_STATE_LABEL_KEYS[m.readinessState])}
                </Badge>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * How a readiness state reads as a badge.
 *
 * Overdue and Missing are the two that need acting on, so both are destructive: R8.3
 * distinguishes them by *why* (late versus never recorded) and a reader needs both to
 * stand out. Completed is settled, and the middle two are neutral.
 */
function readinessBadgeVariant(
  state: ReadinessStateValue,
): 'secondary' | 'destructive' | 'outline' {
  switch (state) {
    case 'completed':
      return 'secondary';
    case 'overdue':
    case 'missing':
      return 'destructive';
    default:
      return 'outline';
  }
}

/**
 * The Dependencies tab: a table of responsible function, description, owner, due
 * date and open/closed state (R12.3), matching the dummy's `Area · Dependency ·
 * Owner · Due` columns with the open state added as its own column.
 *
 * `isOpen` is the server's own derivation, the same predicate the Calendar_View's
 * open-dependency filter evaluates, so the panel and that filter cannot disagree.
 */
function DependenciesTab({ detail }: { readonly detail: InitiativeDetailResponse }): React.JSX.Element {
  const { t } = useTranslation();
  const none = t('detail.value.none');

  if (detail.dependencies.length === 0) {
    return <p className="text-muted-foreground text-[12.3px]">{t('detail.dependencies.empty')}</p>;
  }

  return (
    <Table data-testid="detail-dependencies">
      <TableHeader>
        <TableRow>
          <TableHead scope="col">{t('detail.dependencies.colArea')}</TableHead>
          <TableHead scope="col">{t('detail.dependencies.colDependency')}</TableHead>
          <TableHead scope="col">{t('detail.dependencies.colOwner')}</TableHead>
          <TableHead scope="col">{t('detail.dependencies.colDue')}</TableHead>
          <TableHead scope="col">{t('detail.dependencies.colState')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {detail.dependencies.map((d) => (
          <TableRow key={d.id}>
            <TableCell className="text-txt font-medium">
              {t(`detail.dependencies.function.${d.dependencyFunction}`)}
            </TableCell>
            <TableCell className="whitespace-normal">{d.description ?? none}</TableCell>
            <TableCell>{d.ownerDisplayName ?? d.ownerText ?? none}</TableCell>
            <TableCell className="tabular-nums">
              {d.dueDate === null
                ? none
                : d.dueEndDate === null
                  ? formatDate(d.dueDate)
                  : formatDateRange(d.dueDate, d.dueEndDate)}
            </TableCell>
            <TableCell>
              <Badge variant={d.isOpen ? 'outline' : 'secondary'}>
                {d.isOpen ? t('detail.dependencies.open') : t('detail.dependencies.closed')}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * The History tab: the change-history timeline and the Input_Request list, each a
 * paginated collection with a "load 25 more" control (R12.5, R12.6). The timeline
 * follows UI spec §7: a 2px left rail with accent dots.
 */
function HistoryTab({ initiativeId }: { readonly initiativeId: string }): React.JSX.Element {
  const { t } = useTranslation();
  const history = useChangeHistory(initiativeId);
  const inputRequests = useInputRequests(initiativeId);

  const historyItems = flattenPages<ChangeEvent>(history.data).items;
  const inputRequestItems = flattenPages<InputRequestDto>(inputRequests.data).items;

  return (
    <div className="space-y-6">
      <section data-testid="detail-history">
        {historyItems.length === 0 ? (
          <p className="text-muted-foreground text-[12.3px]">{t('detail.history.empty')}</p>
        ) : (
          <ol className="border-line ml-1 space-y-4 border-l-2 pl-4">
            {historyItems.map((event) => (
              <HistoryEntry key={event.id} event={event} />
            ))}
          </ol>
        )}
        {history.hasNextPage ? (
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            data-testid="detail-history-load-more"
            disabled={history.isFetchingNextPage}
            onClick={() => void history.fetchNextPage()}
          >
            {t('detail.history.loadMore')}
          </Button>
        ) : null}
      </section>

      <section data-testid="detail-input-requests">
        <h3 className="text-[12.3px] font-semibold text-txt">{t('detail.tab.inputRequests')}</h3>
        {inputRequestItems.length === 0 ? (
          <p className="text-muted-foreground mt-1 text-[12.3px]">
            {t('detail.history.inputRequestsEmpty')}
          </p>
        ) : (
          <ul className="mt-2 space-y-3 text-[12.3px]">
            {inputRequestItems.map((request) => (
              <li key={request.id} className="border-line-soft border-b pb-2">
                <p className="text-muted-foreground text-[11.5px]">
                  {t('detail.value.byAt', {
                    actor: request.actorDisplayName,
                    at: formatDate(request.submittedAt.slice(0, 10)),
                  })}
                </p>
                <p className="text-txt">{request.text}</p>
              </li>
            ))}
          </ul>
        )}
        {inputRequests.hasNextPage ? (
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            data-testid="detail-input-requests-load-more"
            disabled={inputRequests.isFetchingNextPage}
            onClick={() => void inputRequests.fetchNextPage()}
          >
            {t('detail.history.loadMore')}
          </Button>
        ) : null}
      </section>
    </div>
  );
}

/** One change-history timeline entry: an accent dot, the change description and the actor/timestamp/reason line. */
function HistoryEntry({ event }: { readonly event: ChangeEvent }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <li className="relative">
      <span
        aria-hidden
        className="bg-accent-line absolute top-1 -left-[22px] size-[9px] rounded-full ring-2 ring-white"
      />
      <p className="text-txt font-medium">
        {t('detail.history.changedField', {
          object: event.objectType,
          field: event.fieldPath ?? event.eventType,
        })}
      </p>
      <p className="text-muted-foreground text-[11.5px]">
        {t('detail.value.byAt', {
          actor: event.actorDisplayName ?? t('detail.value.none'),
          at: formatDate(event.occurredAt.slice(0, 10)),
        })}
        {event.reason !== null ? ` · ${event.reason}` : ''}
      </p>
    </li>
  );
}

/**
 * The fixed footer. Renders only the actions the server offered in
 * `availableActions`, which is capability-gated: a Read_Only_User receives
 * `['input_request']` alone, so **no invokable mutation control** is rendered
 * for them (R12.7, R12.8). The footer double-guards by filtering to the
 * renderable action set, so a future action added to the enum does not surface a
 * control here until this component is taught to render it.
 *
 * Edit navigates to the edit route; Request input, Cancel, Delete and each
 * lifecycle transition open their own confirmation/input dialog and drive one
 * mutation from `detail-actions.ts`. (Duplicate is intentionally not offered
 * here.) Only one dialog is open at a time (`openDialog`), since the actions are
 * mutually exclusive for a given click.
 */
function PanelFooter({
  detail,
  initiativeId,
  onClosePanel,
  onEdit,
}: {
  readonly detail: InitiativeDetailResponse;
  readonly initiativeId: string;
  readonly onClosePanel: () => void;
  readonly onEdit: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState<InitiativeAction | null>(null);
  // The transition the user is confirming, kept separate from `openDialog`
  // because one `transition` action expands into several target buttons and the
  // dialog needs to know which target (and whether it needs a reason).
  const [pendingTransition, setPendingTransition] = useState<{
    readonly target: InitiativeStatus;
    readonly requiresReason: boolean;
  } | null>(null);

  const session = useSession();
  const hasApproverRights = session.data?.approverRights ?? false;

  const submitInputRequest = useSubmitInputRequest(initiativeId);
  const cancelInitiative = useCancelInitiative(initiativeId, detail.version);
  const deleteInitiative = useDeleteInitiative(initiativeId, detail.version);
  const transitionInitiative = useTransitionInitiative(initiativeId, detail.version);

  const rendered = RENDERABLE_ACTIONS.filter((action) => detail.availableActions.includes(action));
  // `transition` is offered by the server as one capability-gated action; the
  // footer expands it into the concrete moves permitted from the current status
  // for this principal's approver rights. Empty (e.g. a Cancelled record, whose
  // only forward move is the Draft restore handled elsewhere, or a non-approver
  // sitting on Planned) simply renders no transition buttons.
  const transitionTargets = rendered.includes('transition')
    ? permittedTransitionTargets(detail.status, hasApproverRights)
    : [];

  const closeDialog = (): void => {
    setOpenDialog(null);
    setPendingTransition(null);
    submitInputRequest.reset();
    cancelInitiative.reset();
    deleteInitiative.reset();
    transitionInitiative.reset();
  };

  return (
    <>
      <footer
        data-testid="detail-footer"
        className="border-line bg-surface-alt shrink-0 border-t px-4 py-3"
      >
        <div className="flex flex-wrap gap-2">
          {rendered.map((action) => {
            // `transition` is not a single button; it becomes one button per
            // permitted target, rendered below.
            if (action === 'transition') {
              return transitionTargets.map(({ target, requiresReason }) => (
                <Button
                  key={`transition-${target}`}
                  data-testid={`detail-action-transition-${target}`}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setOpenDialog('transition');
                    setPendingTransition({ target, requiresReason });
                  }}
                >
                  {t('detail.action.transitionTo', { status: t(STATUS_LABEL_KEYS[target]) })}
                </Button>
              ));
            }
            return (
              <Button
                key={action}
                data-testid={`detail-action-${action}`}
                variant={
                  action === 'edit' ? 'default' : action === 'delete' ? 'destructive' : 'outline'
                }
                size="sm"
                onClick={() => {
                  if (action === 'edit') {
                    onEdit();
                    return;
                  }
                  setOpenDialog(action);
                }}
              >
                {actionLabel(t, action)}
              </Button>
            );
          })}
        </div>
      </footer>

      <InputRequestDialog
        isOpen={openDialog === 'input_request'}
        isSubmitting={submitInputRequest.isPending}
        hasError={submitInputRequest.isError}
        onSubmit={(text) => {
          submitInputRequest.mutate(text, { onSuccess: closeDialog });
        }}
        onClose={closeDialog}
      />

      <CancelInitiativeDialog
        isOpen={openDialog === 'cancel'}
        isSubmitting={cancelInitiative.isPending}
        hasError={cancelInitiative.isError}
        onConfirm={(reason) => {
          cancelInitiative.mutate(reason, { onSuccess: closeDialog });
        }}
        onClose={closeDialog}
      />

      <DeleteInitiativeDialog
        isOpen={openDialog === 'delete'}
        isSubmitting={deleteInitiative.isPending}
        hasError={deleteInitiative.isError}
        onConfirm={() => {
          deleteInitiative.mutate(undefined, {
            onSuccess: () => {
              closeDialog();
              onClosePanel();
            },
          });
        }}
        onClose={closeDialog}
      />

      <TransitionDialog
        isOpen={openDialog === 'transition' && pendingTransition !== null}
        targetStatus={pendingTransition?.target ?? null}
        requiresReason={pendingTransition?.requiresReason ?? false}
        isSubmitting={transitionInitiative.isPending}
        hasError={transitionInitiative.isError}
        onConfirm={({ reason, comment }) => {
          if (pendingTransition === null) return;
          transitionInitiative.mutate(
            { targetStatus: pendingTransition.target, reason, comment },
            { onSuccess: closeDialog },
          );
        }}
        onClose={closeDialog}
      />
    </>
  );
}

/**
 * The R13.5 indication that the calendar entry the panel opened from was outdated.
 * The calendar bar is being re-rendered from current values in the background
 * (the freshness hook has already invalidated the calendar read); this states, in
 * words, that the rendered bar predated the current values.
 */
function OutdatedIndication(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Alert variant="destructive" data-testid="detail-outdated" className="mb-4">
      <AlertTitle>{t('detail.outdated.title')}</AlertTitle>
      <AlertDescription>{t('detail.outdated.body')}</AlertDescription>
    </Alert>
  );
}

/** The label for one footer action. */
function actionLabel(t: TFunction, action: InitiativeAction): string {
  switch (action) {
    case 'edit':
      return t('detail.action.edit');
    case 'duplicate':
      return t('detail.action.duplicate');
    case 'cancel':
      return t('detail.action.cancel');
    case 'delete':
      return t('detail.action.delete');
    default:
      return t('detail.action.inputRequest');
  }
}

/** An owner's display name with the R4.11 inactive-account marker appended when the stored account is inactive. */
function ownerLabel(t: TFunction, displayName: string, active: boolean): string {
  return active ? displayName : `${displayName} ${t('detail.value.inactiveAccount')}`;
}

/** A comma-joined id set, or the empty placeholder when the set is empty. */
function setLabel(ids: readonly string[], none: string): string {
  return ids.length === 0 ? none : ids.join(', ');
}

/** The involvement summary for the Overview kv row. */
function involvementLabel(t: TFunction, detail: InitiativeDetailResponse): string {
  const parts: string[] = [];
  if (detail.fieldForceInvolvement) parts.push(t('detail.involvement.fieldForce'));
  if (detail.dteInvolvement) parts.push(t('detail.involvement.dte'));
  if (detail.cscInvolvement) parts.push(t('detail.involvement.csc'));
  return parts.length === 0 ? t('detail.involvement.none') : parts.join(', ');
}
